import * as React from 'react';

import {
  maybeParseCalendarDate,
  toISODateString,
} from '@rematter/common/dateTime';
import {
  CurrencyCode,
  enumBookingStatus,
  enumCurrencyCode,
  enumLoadStatus,
  enumSalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import {
  DatePicker,
  ContentSection,
  Grid,
  SmartDatePicker,
  cn,
  ComboBoxRef,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/vanilla/utils';
import isEqual from 'lodash/isEqual';

import { $loadInfoFocusSignal } from '../../_state/loadsTab.state';

import {
  $loadForm,
  $selectedSalesOrderId,
  $selectedBookingId,
} from '~/modules/trade/_state/loadForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormState, LoadRecord } from '~/modules/trade/types/load';
import { loadHasBeenShipped } from '~/modules/trade/utils/load';
import { SelectBooking } from '~/shared/components/select-fields/SelectBooking';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectOutboundTollingAgreement } from '~/shared/components/select-fields/SelectOutboundTollingAgreement';
import {
  SelectSalesOrder,
  SelectableSalesOrder,
} from '~/shared/components/select-fields/SelectSalesOrder';

type LoadBasicInfoFieldsProps = {
  className?: string;
  disabled: boolean;
  requiredBookingId?: string;
  requiredSalesOrderId?: string;
  load?: LoadRecord | LoadFormState | null;
  isDonePopulatingRef?: React.RefObject<boolean>;
};

const loadBasicInfoFieldsSelector = (state: LoadFormState) => ({
  expectedShipDate: state.expectedShipDate,
  facilityId: state.facilityId,
  currencyCode: state.currencyCode,
  status: state.status,
  bookingId: state.bookingId,
  loadReceivedDate: state.loadReceivedDate,
  shippedDate: state.shippedDate,
  salesOrderNumber: state.salesOrderNumber,
  salesOrderId: state.salesOrderId,
  outboundTollingAgreementId: state.outboundTollingAgreementId,
});

const FOCUS_DELAY_MS = 50;

export const LoadBasicInfoFields = ({
  className,
  disabled,
  requiredBookingId,
  requiredSalesOrderId,
}: LoadBasicInfoFieldsProps) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($loadForm, loadBasicInfoFieldsSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);

  const updateForm = useSetAtom($loadForm);

  // 🟣🟣🟣 Focus signal for sales order/booking when navigating through redirect
  const salesOrderRef = React.useRef<ComboBoxRef | null>(null);
  const bookingsRef = React.useRef<ComboBoxRef | null>(null);
  const [focusSignal, setFocusSignal] = useAtom($loadInfoFocusSignal);

  const selectedSalesOrderId = useAtomValue($selectedSalesOrderId);
  const selectedBookingId = useAtomValue($selectedBookingId);
  const setSelectedBookingId = useSetAtom($selectedBookingId);
  const setSelectedSalesOrderId = useSetAtom($selectedSalesOrderId);

  React.useEffect(() => {
    if (!focusSignal || disabled) return;

    const shouldFocusSalesOrder =
      focusSignal === 'salesOrder' && !selectedSalesOrderId;
    const shouldFocusBooking =
      !selectedBookingId &&
      (focusSignal === 'booking' || !shouldFocusSalesOrder);

    if (shouldFocusSalesOrder) {
      setTimeout(() => salesOrderRef.current?.focus(), FOCUS_DELAY_MS);
    } else if (shouldFocusBooking) {
      setTimeout(() => bookingsRef.current?.focus(), FOCUS_DELAY_MS);
    }

    setFocusSignal(null);
  }, [
    focusSignal,
    disabled,
    selectedSalesOrderId,
    selectedBookingId,
    setFocusSignal,
  ]);

  // Set the required IDs in the stable atoms
  React.useEffect(() => {
    if (requiredSalesOrderId) {
      setSelectedSalesOrderId(requiredSalesOrderId);
    }
  }, [requiredSalesOrderId, setSelectedSalesOrderId]);

  React.useEffect(() => {
    if (requiredBookingId) {
      setSelectedBookingId(requiredBookingId);
    }
  }, [requiredBookingId, setSelectedBookingId]);

  const hasBeenShipped = loadHasBeenShipped(form.status);

  const handleSalesOrderSelectChange = (
    value: string | undefined,
    salesOrder?: SelectableSalesOrder,
  ) => {
    const salesOrderId = value?.toString();

    // Update the stable sales order ID atom to trigger the query
    setSelectedSalesOrderId(salesOrderId);

    // Update the form state
    updateForm((prev) => ({
      ...prev,
      salesOrderId,
      salesOrderNumber: salesOrder?.documentNumber ?? undefined,
    }));
  };

  const handleDateChange = (value: string | null) => {
    updateForm((prev) => ({
      ...prev,
      expectedShipDate: value ? maybeParseCalendarDate(value) : undefined,
    }));
  };

  const handleShippedDateChange = (value: string | null) => {
    updateForm((prev) => ({
      ...prev,
      shippedDate: value ? maybeParseCalendarDate(value) : undefined,
    }));
  };

  const handleLoadReceivedDateChange = (value: string | null) => {
    updateForm((prev) => ({
      ...prev,
      loadReceivedDate: value ? maybeParseCalendarDate(value) : undefined,
    }));
  };

  const handleSelectChange = React.useCallback(
    (field: keyof typeof form) => (value: string | undefined) => {
      const fieldValue = value?.toString() || undefined;

      // Update stable atoms for booking changes
      if (field === 'bookingId') {
        setSelectedBookingId(fieldValue);
      }

      updateForm((prev) => ({
        ...prev,
        [field]: fieldValue,
      }));
    },
    [updateForm, setSelectedBookingId],
  );

  const bookingValidStatus = React.useMemo(() => [enumBookingStatus.Open], []);

  const handleCurrencyChange = React.useCallback(
    (value: string | undefined) => {
      updateForm((prev) => ({
        ...prev,
        currencyCode: (value as CurrencyCode) ?? enumCurrencyCode.USD,
      }));
    },
    [updateForm],
  );

  return (
    <ContentSection
      className={cn(
        '@LoadBasicInfoFields',
        cn('@LoadBasicInfoEntitiesAndDatesForm', className),
      )}
    >
      <Grid cols4 gap3 className={cn(hasBeenShipped && 'grid-cols-3')}>
        <SelectSalesOrder
          fieldRef={salesOrderRef as React.RefObject<ComboBoxRef>}
          value={requiredSalesOrderId ?? selectedSalesOrderId ?? undefined}
          label={t.loadsView.form.salesOrderId}
          statuses={[enumSalesOrderStatus.Open]}
          onChange={handleSalesOrderSelectChange}
          openingTriggers={['click', 'focus']}
          isDisabled={disabled || Boolean(requiredSalesOrderId)}
        />
        <SelectBooking
          fieldRef={bookingsRef as React.RefObject<ComboBoxRef>}
          value={requiredBookingId ?? selectedBookingId ?? undefined}
          label={t.loadsView.form.bookingId}
          statuses={bookingValidStatus}
          onChange={handleSelectChange('bookingId')}
          openingTriggers={['click', 'focus']}
          isDisabled={disabled || Boolean(requiredBookingId)}
          clearable
        />
        {!hasBeenShipped && (
          <SmartDatePicker
            value={form.expectedShipDate}
            label={t.loadsView.form.expectedShipDate}
            onChange={(date) =>
              handleDateChange(date ? toISODateString(date) : null)
            }
            isRequired={true}
            isDisabled={disabled}
          />
        )}
        <SelectFacility
          value={form.facilityId}
          label={t.loadsView.form.facilityLocationId}
          onChange={handleSelectChange('facilityId')}
          isRequired={true}
          autoSelectFirstOption
          isDisabled={disabled}
        />
        <SelectCurrency
          value={form.currencyCode ?? enumCurrencyCode.USD}
          label={t.loadsView.form.currency}
          onChange={handleCurrencyChange}
          isDisabled={disabled}
        />
        <SelectOutboundTollingAgreement
          value={form.outboundTollingAgreementId ?? undefined}
          label={t.loadsView.form.outboundTollingAgreement}
          onChange={handleSelectChange('outboundTollingAgreementId')}
          isDisabled={disabled}
          clearable
        />
        {hasBeenShipped && (
          <>
            <DatePicker
              value={form.expectedShipDate}
              label={t.loadsView.form.expectedShipDate}
              onChange={(date) =>
                handleDateChange(date ? toISODateString(date) : null)
              }
              isRequired={true}
              isDisabled={disabled}
            />
            <DatePicker
              value={form.shippedDate ?? undefined}
              label={t.loadsView.form.shippedDate}
              onChange={(date) =>
                handleShippedDateChange(date ? toISODateString(date) : null)
              }
              isRequired={true}
              isDisabled
            />
            {(form.status === enumLoadStatus.Received ||
              form.status === enumLoadStatus.Reconciled) && (
              <DatePicker
                value={form.loadReceivedDate ?? undefined}
                label={t.loadsView.form.loadReceivedDate}
                onChange={(date) =>
                  handleLoadReceivedDateChange(
                    date ? toISODateString(date) : null,
                  )
                }
                isRequired={true}
                isDisabled
              />
            )}
          </>
        )}
      </Grid>
    </ContentSection>
  );
};
